import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { LnPrimaryButtonComponent } from '@components/butons/ln-primary-button/ln-primary-button.component';
import { MainProductCardComponent } from '@components/cards/main-product-card/main-product-card.component';
import { SpecialProductInterface } from '@interfaces/products/special_product_interface';
import { ProductServiceParamsInterface } from '@interfaces/products/product_service_params_interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from '@interfaces/api/api.interface';
import { GetSpecialProductsData } from '@interfaces/products/get_special_products_data';
import { ProductsService } from '@core/services/products/products.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { DeviceScreenFamilyService } from '@core/services/device-screen-family/device-screen-family.service';
import { ObjectDataState } from '../../states/object-data-State';
import { SpecialProductsObjectDataStateInterface } from '@interfaces/special-products/special-products-object-data-state-interface';
import { SortFilter } from '@enums/sortFilter';

@Component({
  selector: 'ln-artisans-designers-created-and-sold-products',
  imports: [LnPrimaryButtonComponent, MainProductCardComponent, TranslocoPipe],
  templateUrl: './artisans-designers-created-and-sold-products.component.html',
  styleUrl: './artisans-designers-created-and-sold-products.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArtisansDesignersCreatedAndSoldProductsComponent implements OnInit {
  readonly TAG = 'ArtisansDesignersCreatedAndSoldProducts';
  products: WritableSignal<SpecialProductInterface[]> = signal<SpecialProductInterface[]>([]);
  pageNumber = 1;
  pageSize = 24;
  showMoreProducts = signal(false);
  protected showMoreProductsButonSpinner: WritableSignal<boolean> = signal(false);
  protected gettingProducts: WritableSignal<boolean> = signal(true);
  private productService = inject(ProductsService);
  private destroyRef = inject(DestroyRef);
  private deviceScreenFamily = inject(DeviceScreenFamilyService);
  protected currentScreenSize = this.deviceScreenFamily.currentDeviceScreenFamily;
  private objectDataState: ObjectDataState<SpecialProductsObjectDataStateInterface> = inject(ObjectDataState);

  ngOnInit(): void {
    this.restoreObjectDataState();
  }

  updateObjectDataState() {
    const specialProductInterface: SpecialProductsObjectDataStateInterface = {
      selectedFilter: SortFilter.mayorPrecio,
      pageNumber: this.pageNumber,
      showMoreProducts: this.showMoreProducts(),
    };
    this.objectDataState.setData(specialProductInterface);
  }

  restoreObjectDataState() {
    const specialProductData: SpecialProductsObjectDataStateInterface | undefined = this.objectDataState.getData();
    if (specialProductData) {
      this.pageNumber = specialProductData.pageNumber;
      this.showMoreProducts.set(specialProductData.showMoreProducts);
    }
    this.getProducts();
  }

  getProducts() {
    // console.log(`${this.TAG} getProducts(getMoreProducts= ${getMoreProducts})`);
    const querryParams: ProductServiceParamsInterface = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      dateOrder: 'desc',
      isPiezaUnica: true,
    };
    //si se produjo un cambio de filtro o se esta restableciendo el estado anterior de la pagina
    let usedPageSize = this.pageSize;
    if (this.pageNumber > 1) {
      querryParams.pageSize = this.pageNumber * this.pageSize;
      usedPageSize = querryParams.pageSize;
      querryParams.pageNumber = 1;
      this.products.set([]);
    }
    this.gettingProducts.set(true);
    this.showMoreProducts.set(false);
    this.productService
      .getProducts(querryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<GetSpecialProductsData>) => {
          const productos = res.data.productos;
          const total = res.data.total;
          this.products.set(productos);
          if (productos.length >= usedPageSize && usedPageSize * this.pageNumber < total) {
            this.showMoreProducts.set(true);
          } else {
            this.showMoreProducts.set(false);
          }
          this.updateObjectDataState();
          this.gettingProducts.set(false);
        },
        error: err => {
          console.log(`${this.TAG} ${err}`);
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
      });
  }

  getMoreProducts() {
    this.showMoreProductsButonSpinner.set(true);
    this.pageNumber++;
    const querryParams: ProductServiceParamsInterface = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      dateOrder: 'desc',
      isPiezaUnica: true,
    };
    this.productService
      .getProducts(querryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse<GetSpecialProductsData>) => {
          const productos = res.data.productos;
          const total = res.data.total;
          this.products.set([...this.products(), ...productos]);
          if (productos.length >= this.pageSize && this.pageSize * this.pageNumber < total) {
            this.showMoreProducts.set(true);
          } else {
            this.showMoreProducts.set(false);
          }
          this.updateObjectDataState();
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
        error: err => {
          console.log(`${this.TAG} ${err}`);
          this.showMoreProductsButonSpinner.set(false);
          this.gettingProducts.set(false);
        },
      });
  }
}
